import React, { useState, useEffect } from 'react'
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Snackbar,
  Button,
  Box,
  Divider,
  Chip,
  Paper,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { 
  Error as ErrorIcon, 
  TrendingUp,
  Today,
  CalendarToday,
  ShowChart,
  Refresh,
  Receipt,
  CheckCircle,
  Cancel,
  People,
  Business
} from '@material-ui/icons'
import CountUp from 'react-countup'
import BonClientService from '../../services/BonClientService';
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fafafa',
    minHeight: '100vh',
    padding: theme.spacing(3)
  },
  header: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  },
  headerTitle: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1.8rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  headerSubtitle: {
    color: '#666',
    fontSize: '1rem'
  },
  periodCard: {
    height: '100%',
    borderRadius: 12,
    backgroundColor: 'white',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.12)'
    }
  },
  periodHeader: {
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #eaeaea',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12
  },
  periodTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: '#444',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  periodIcon: {
    color: '#4CAF50',
    backgroundColor: '#E8F5E9',
    borderRadius: '50%',
    padding: theme.spacing(0.75),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  periodChip: {
    backgroundColor: '#4CAF50',
    color: 'white',
    fontWeight: 600,
    fontSize: '0.8rem',
    marginLeft: 'auto'
  },
  
  // Section des totaux
  totalsSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    backgroundColor: '#f8f9fa',
    borderRadius: 10,
    border: '1px solid #e0e0e0'
  },
  totalsTitle: {
    fontWeight: 600,
    color: '#444',
    fontSize: '1rem',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  totalsVerticalGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5)
  },
  totalVerticalItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: 'white',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
  },
  totalLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#666'
  },
  totalValue: {
    fontSize: '1.1rem',
    fontWeight: 700
  },
  
  // Cartes pour Payés et Impayés
  statutCard: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderTop: '4px solid',
    height: '100%',
    minHeight: 180
  },
  payeCard: {
    borderTopColor: '#4CAF50'
  },
  impayeCard: {
    borderTopColor: '#F44336'
  },
  statutHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  statutIcon: {
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%'
  },
  payeIcon: {
    backgroundColor: '#E8F5E9',
    color: '#4CAF50'
  },
  impayeIcon: {
    backgroundColor: '#FFEBEE',
    color: '#F44336'
  },
  statutTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: '#444'
  },
  statutChip: {
    marginLeft: 'auto'
  },
  
  // Table pour répartition par type
  tableContainer: {
    marginTop: theme.spacing(3),
    borderRadius: 8,
    overflow: 'hidden'
  },
  tableHeader: {
    backgroundColor: '#f5f5f5'
  },
  tableHeaderCell: {
    fontWeight: 700,
    color: '#333',
    fontSize: '0.85rem'
  },
  tableRow: {
    '&:nth-of-type(even)': {
      backgroundColor: '#fafafa'
    }
  },
  typeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  
  // Cartes verticales pour montants
  montantVerticalContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2)
  },
  montantVerticalItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    backgroundColor: '#f8f9fa',
    borderRadius: 8
  },
  montantVerticalLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  montantVerticalValue: {
    fontSize: '0.9rem',
    fontWeight: 700
  },
  htValue: { color: '#1976d2' },
  tvaValue: { color: '#2e7d32' },
  ttcValue: { color: '#ef6c00' },
  
  // Stats info
  infoStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '0.8rem',
    color: '#666'
  },
  
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
    gap: theme.spacing(2)
  },
  loadingText: {
    color: '#666',
    fontWeight: 500
  },
  errorContainer: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    backgroundColor: '#FFEBEE',
    borderRadius: 8,
    border: '1px solid #FFCDD2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  errorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  errorIcon: {
    color: '#F44336'
  },
  errorText: {
    fontWeight: 500,
    color: '#444'
  },
  retryButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    padding: theme.spacing(0.5, 1.5),
    '&:hover': {
      backgroundColor: '#45a049'
    }
  },
  
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: '#888'
  },
  emptyIcon: {
    fontSize: 48,
    color: '#BDBDBD',
    marginBottom: theme.spacing(2)
  }
}));

const getDefaultStats = () => ({
    totaux: {
      payes: { 
        nombre_bons: 0, 
        nombre_clients: 0,
        montant_total: 0, 
        total_ht: 0, 
        total_tva: 0, 
        total_ttc: 0,
        total_remise: 0,
        taux_tva: 18
      },
      impayes: { 
        nombre_bons: 0, 
        nombre_clients: 0,
        montant_total: 0, 
        total_ht: 0, 
        total_tva: 0, 
        total_ttc: 0,
        total_remise: 0,
        taux_tva: 18
      },
      global: { 
        nombre_bons: 0, 
        nombre_clients: 0,
        montant_total: 0, 
        total_ht: 0, 
        total_tva: 0, 
        total_ttc: 0,
        total_remise: 0
      }
    },
    repartition_par_type: []
  });

function StatsBonClient() {
  const classes = useStyles();
  const user = useSelector((state) => state.auth);
  const theme = useTheme();
  const userRole = user?.rol; 

  const [stats, setStats] = useState({
    aujourdhui: getDefaultStats(),
    hier: getDefaultStats(),
    mois_courant: getDefaultStats(),
    annee_courante: getDefaultStats()
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);



  const getPeriodes = () => {
    const basePeriodes = [
      { key: 'aujourdhui', title: "Aujourd'hui", icon: <Today /> },
      { key: 'hier', title: 'Hier', icon: <CalendarToday /> }
    ];
    
    if (userRole === 'admin') {
      basePeriodes.push(
        { key: 'mois_courant', title: 'Ce mois', icon: <ShowChart /> },
        { key: 'annee_courante', title: 'Cette année', icon: <TrendingUp /> }
      );
    }
    
    return basePeriodes;
  };

  const periodes = getPeriodes();

  useEffect(() => {
    fetchData();
  }, [userRole]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await BonClientService.geSynthese();
      
      if (response.data) {
        const normalizedStats = {};
        periodes.forEach(periode => {
          const periodeData = response.data[periode.key];
          if (periodeData) {
            normalizedStats[periode.key] = periodeData;
          } else {
            normalizedStats[periode.key] = getDefaultStats();
          }
        });
        
        setStats(normalizedStats);
        setError(null);
      } else {
        throw new Error('Aucune donnée reçue du serveur');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des stats BonClient:', error);
      setError('Impossible de charger les statistiques des bons clients');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const FormattedMontant = ({ montant, variant = 'body2', color = 'inherit', bold = true, showSymbol = true }) => {
    return (
      <Typography variant={variant} style={{ color, fontWeight: bold ? 700 : 400 }}>
        <CountUp 
          start={0} 
          end={montant || 0} 
          duration={1.5} 
          separator=" " 
          decimals={2}
          decimal=","
          suffix={showSymbol ? " F CFA" : ""}
        />
      </Typography>
    );
  };

  const getTypeIcon = (type) => {
    if (type?.includes('PARTICULIER') || type?.includes('INDIVIDUEL')) {
      return <People />;
    } else if (type?.includes('ENTREPRISE') || type?.includes('SOCIETE')) {
      return <Business />;
    }
    return <People />;
  };

  const handleRetry = () => {
    fetchData();
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={50} style={{ color: '#4CAF50' }} />
        <Typography className={classes.loadingText}>
          Chargement des données...
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography variant="h5" className={classes.headerTitle}>
            <Receipt style={{ fontSize: 28 }} />
            Tableau de Bord - Bons Clients
          </Typography>
          <Typography className={classes.headerSubtitle}>
            Analyse des bons clients payés et impayés avec TVA
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Refresh />}
          onClick={handleRetry}
          style={{ backgroundColor: '#4CAF50' }}
        >
          Actualiser
        </Button>
      </Box>

      {error && (
        <Paper className={classes.errorContainer} elevation={0}>
          <Box className={classes.errorContent}>
            <ErrorIcon className={classes.errorIcon} />
            <Typography className={classes.errorText}>
              {error}
            </Typography>
          </Box>
          <Button 
            className={classes.retryButton}
            onClick={handleRetry}
            startIcon={<Refresh />}
          >
            Réessayer
          </Button>
        </Paper>
      )}

      {/* Cartes des périodes */}
      <Grid container spacing={3}>
        {periodes.map((periode) => {
          const periodeData = stats[periode.key] || getDefaultStats();
          const totaux = periodeData.totaux || {};
          const repartition = periodeData.repartition_par_type || [];
          
          return (
            <Grid item xs={12} md={6} lg={4} key={periode.key}>
              <Card className={classes.periodCard} elevation={0}>
                <CardContent style={{ padding: 0 }}>
                  {/* En-tête de la période */}
                  <Box className={classes.periodHeader}>
                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography className={classes.periodTitle}>
                        <Box className={classes.periodIcon}>
                          {periode.icon}
                        </Box>
                        {periode.title}
                      </Typography>
                      <Chip 
                        label={`${totaux.global?.nombre_bons || 0} bons`}
                        size="small"
                        className={classes.periodChip}
                      />
                    </Box>
                  </Box>

                  <Box style={{ padding: theme.spacing(2) }}>
                    {totaux.global?.nombre_bons > 0 || totaux.global?.montant_total > 0 ? (
                      <>
                        {/* Cartes Payés et Impayés */}
                        <Grid container spacing={2}>
                          {/* Carte Payés */}
                          <Grid item xs={6}>
                            <Paper className={`${classes.statutCard} ${classes.payeCard}`}>
                              <Box className={classes.statutHeader}>
                                <Box className={`${classes.statutIcon} ${classes.payeIcon}`}>
                                  <CheckCircle />
                                </Box>
                                <Typography className={classes.statutTitle}>
                                  Payés
                                </Typography>
                                <Chip 
                                  label={totaux.payes?.nombre_bons || 0}
                                  size="small"
                                  className={classes.statutChip}
                                  style={{ backgroundColor: '#4CAF50', color: 'white' }}
                                />
                              </Box>
                              
                              {/* Stats info */}
                              <Box className={classes.infoStats}>
                                <Box className={classes.infoItem}>
                                  <Typography variant="caption">Clients:</Typography>
                                  <Typography variant="caption" style={{ fontWeight: 600 }}>
                                    {totaux.payes?.nombre_clients || 0}
                                  </Typography>
                                </Box>
                                <Box className={classes.infoItem}>
                                  <Typography variant="caption">Remise:</Typography>
                                  <FormattedMontant 
                                    montant={totaux.payes?.total_remise || 0}
                                    variant="caption"
                                    color="#888"
                                    bold={false}
                                  />
                                </Box>
                              </Box>
                              
                              {/* Montants verticaux pour Payés */}
                              <Box className={classes.montantVerticalContainer}>
                                {/* HT */}
                                <Box className={classes.montantVerticalItem}>
                                  <Typography className={classes.montantVerticalLabel}>
                                    HT
                                  </Typography>
                                  <FormattedMontant 
                                    montant={totaux.payes?.total_ht || 0}
                                    variant="body2"
                                    color="#1976d2"
                                  />
                                </Box>
                                
                                {/* TVA */}
                                <Box className={classes.montantVerticalItem}>
                                  <Box style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                                    <Typography className={classes.montantVerticalLabel}>
                                      TVA
                                    </Typography>
                                    <Typography variant="caption" style={{ color: '#2e7d32' }}>
                                      ({totaux.payes?.taux_tva || 18}%)
                                    </Typography>
                                  </Box>
                                  <FormattedMontant 
                                    montant={totaux.payes?.total_tva || 0}
                                    variant="body2"
                                    color="#2e7d32"
                                  />
                                </Box>
                                
                                {/* TTC */}
                                <Box className={classes.montantVerticalItem}>
                                  <Typography className={classes.montantVerticalLabel}>
                                    TTC
                                  </Typography>
                                  <FormattedMontant 
                                    montant={totaux.payes?.total_ttc || 0}
                                    variant="body2"
                                    color="#4CAF50"
                                  />
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>

                          {/* Carte Impayés */}
                          <Grid item xs={6}>
                            <Paper className={`${classes.statutCard} ${classes.impayeCard}`}>
                              <Box className={classes.statutHeader}>
                                <Box className={`${classes.statutIcon} ${classes.impayeIcon}`}>
                                  <Cancel />
                                </Box>
                                <Typography className={classes.statutTitle}>
                                  Impayés
                                </Typography>
                                <Chip 
                                  label={totaux.impayes?.nombre_bons || 0}
                                  size="small"
                                  className={classes.statutChip}
                                  style={{ backgroundColor: '#F44336', color: 'white' }}
                                />
                              </Box>
                              
                              {/* Stats info */}
                              <Box className={classes.infoStats}>
                                <Box className={classes.infoItem}>
                                  <Typography variant="caption">Clients:</Typography>
                                  <Typography variant="caption" style={{ fontWeight: 600 }}>
                                    {totaux.impayes?.nombre_clients || 0}
                                  </Typography>
                                </Box>
                                <Box className={classes.infoItem}>
                                  <Typography variant="caption">Remise:</Typography>
                                  <FormattedMontant 
                                    montant={totaux.impayes?.total_remise || 0}
                                    variant="caption"
                                    color="#888"
                                    bold={false}
                                  />
                                </Box>
                              </Box>
                              
                              {/* Montants verticaux pour Impayés */}
                              <Box className={classes.montantVerticalContainer}>
                                {/* HT */}
                                <Box className={classes.montantVerticalItem}>
                                  <Typography className={classes.montantVerticalLabel}>
                                    HT
                                  </Typography>
                                  <FormattedMontant 
                                    montant={totaux.impayes?.total_ht || 0}
                                    variant="body2"
                                    color="#1976d2"
                                  />
                                </Box>
                                
                                {/* TVA */}
                                <Box className={classes.montantVerticalItem}>
                                  <Box style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                                    <Typography className={classes.montantVerticalLabel}>
                                      TVA
                                    </Typography>
                                    <Typography variant="caption" style={{ color: '#2e7d32' }}>
                                      ({totaux.impayes?.taux_tva || 18}%)
                                    </Typography>
                                  </Box>
                                  <FormattedMontant 
                                    montant={totaux.impayes?.total_tva || 0}
                                    variant="body2"
                                    color="#2e7d32"
                                  />
                                </Box>
                                
                                {/* TTC */}
                                <Box className={classes.montantVerticalItem}>
                                  <Typography className={classes.montantVerticalLabel}>
                                    TTC
                                  </Typography>
                                  <FormattedMontant 
                                    montant={totaux.impayes?.total_ttc || 0}
                                    variant="body2"
                                    color="#F44336"
                                  />
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        </Grid>

                        {/* Totaux globaux */}
                        <Box className={classes.totalsSection}>
                          <Typography className={classes.totalsTitle}>
                            <TrendingUp style={{ fontSize: 18 }} />
                            Totaux Globaux
                          </Typography>
                          <Box className={classes.totalsVerticalGrid}>
                            {/* Total HT */}
                            <Box className={classes.totalVerticalItem}>
                              <Typography className={classes.totalLabel}>
                                Total HT
                              </Typography>
                              <FormattedMontant 
                                montant={totaux.global?.total_ht || 0}
                                variant="body1"
                                color="#1976d2"
                              />
                            </Box>
                            
                            {/* Total TVA */}
                            <Box className={classes.totalVerticalItem}>
                              <Typography className={classes.totalLabel}>
                                Total TVA
                              </Typography>
                              <FormattedMontant 
                                montant={totaux.global?.total_tva || 0}
                                variant="body1"
                                color="#2e7d32"
                              />
                            </Box>
                            
                            {/* Total TTC */}
                            <Box className={classes.totalVerticalItem}>
                              <Typography className={classes.totalLabel}>
                                Total TTC
                              </Typography>
                              <FormattedMontant 
                                montant={totaux.global?.total_ttc || 0}
                                variant="h6"
                                color="#ef6c00"
                              />
                            </Box>
                            
                            {/* Total Remise */}
                            <Box className={classes.totalVerticalItem}>
                              <Typography className={classes.totalLabel}>
                                Total Remise
                              </Typography>
                              <FormattedMontant 
                                montant={totaux.global?.total_remise || 0}
                                variant="body1"
                                color="#9C27B0"
                              />
                            </Box>
                          </Box>
                        </Box>

                        {/* Répartition par type (seulement pour aujourd'hui) */}
                        {periode.key === 'aujourdhui' && repartition.length > 0 && (
                          <Box style={{ marginTop: theme.spacing(3) }}>
                            <Typography variant="subtitle2" style={{ 
                              fontWeight: 600, 
                              color: '#444', 
                              marginBottom: theme.spacing(1.5),
                              display: 'flex',
                              alignItems: 'center',
                              gap: theme.spacing(1)
                            }}>
                              <People style={{ fontSize: 20 }} />
                              Répartition par Type de Client
                            </Typography>
                            
                            <TableContainer component={Paper} className={classes.tableContainer}>
                              <Table size="small">
                                <TableHead className={classes.tableHeader}>
                                  <TableRow>
                                    <TableCell className={classes.tableHeaderCell}>Type Client</TableCell>
                                    <TableCell className={classes.tableHeaderCell} align="right">Clients</TableCell>
                                    <TableCell className={classes.tableHeaderCell} align="right">Bons Payés</TableCell>
                                    <TableCell className={classes.tableHeaderCell} align="right">Bons Impayés</TableCell>
                                    <TableCell className={classes.tableHeaderCell} align="right">Total TTC</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {repartition.map((type, index) => (
                                    <TableRow key={index} className={classes.tableRow}>
                                      <TableCell>
                                        <Box className={classes.typeCell}>
                                          {getTypeIcon(type.type_client)}
                                          {type.type_client}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">{type.nombre_clients}</TableCell>
                                      <TableCell align="right">
                                        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                          <Typography variant="caption" style={{ color: '#4CAF50' }}>
                                            {type.nombre_payes} bons
                                          </Typography>
                                          <FormattedMontant 
                                            montant={type.montant_paye}
                                            variant="caption"
                                            color="#4CAF50"
                                            bold={false}
                                            showSymbol={false}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                          <Typography variant="caption" style={{ color: '#F44336' }}>
                                            {type.nombre_impayes} bons
                                          </Typography>
                                          <FormattedMontant 
                                            montant={type.montant_impaye}
                                            variant="caption"
                                            color="#F44336"
                                            bold={false}
                                            showSymbol={false}
                                          />
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <FormattedMontant 
                                          montant={type.total_ttc}
                                          variant="body2"
                                          color="#ef6c00"
                                          showSymbol={false}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box className={classes.emptyState}>
                        <Receipt className={classes.emptyIcon} />
                        <Typography variant="body1" style={{ color: '#888', fontWeight: 500 }}>
                          Aucun bon client enregistré pour cette période
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Résumé comparatif */}
      {periodes.length > 1 && (
        <Paper elevation={0} style={{ marginTop: theme.spacing(4), padding: theme.spacing(3), backgroundColor: 'white', borderRadius: 12 }}>
          <Typography variant="h6" style={{ fontWeight: 600, color: '#333', marginBottom: theme.spacing(3) }}>
            <ShowChart style={{ marginRight: theme.spacing(1), verticalAlign: 'middle' }} />
            Résumé Comparatif
          </Typography>
          <Grid container spacing={2}>
            {periodes.map((periode) => {
              const periodeData = stats[periode.key] || getDefaultStats();
              const totaux = periodeData.totaux || {};
              
              return (
                <Grid item xs={12} sm={6} md={3} key={periode.key}>
                  <Box style={{ padding: theme.spacing(2), backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#444', marginBottom: theme.spacing(1.5) }}>
                      {periode.title}
                    </Typography>
                    
                    {/* Bons Payés */}
                    <Box style={{ marginBottom: theme.spacing(1.5), padding: theme.spacing(1.5), backgroundColor: '#f0f9f0', borderRadius: 6 }}>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                        <Typography variant="caption" style={{ color: '#4CAF50', fontWeight: 600 }}>
                          Payés
                        </Typography>
                        <Box style={{ textAlign: 'right' }}>
                          <Typography variant="caption" style={{ color: '#4CAF50', fontWeight: 600, display: 'block' }}>
                            {totaux.payes?.nombre_bons || 0} bons
                          </Typography>
                          <Typography variant="caption" style={{ color: '#4CAF50', display: 'block' }}>
                            {totaux.payes?.nombre_clients || 0} clients
                          </Typography>
                        </Box>
                      </Box>
                      <FormattedMontant 
                        montant={totaux.payes?.total_ttc || 0}
                        variant="body2"
                        color="#4CAF50"
                      />
                    </Box>
                    
                    {/* Bons Impayés */}
                    <Box style={{ marginBottom: theme.spacing(1.5), padding: theme.spacing(1.5), backgroundColor: '#fff5f5', borderRadius: 6 }}>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                        <Typography variant="caption" style={{ color: '#F44336', fontWeight: 600 }}>
                          Impayés
                        </Typography>
                        <Box style={{ textAlign: 'right' }}>
                          <Typography variant="caption" style={{ color: '#F44336', fontWeight: 600, display: 'block' }}>
                            {totaux.impayes?.nombre_bons || 0} bons
                          </Typography>
                          <Typography variant="caption" style={{ color: '#F44336', display: 'block' }}>
                            {totaux.impayes?.nombre_clients || 0} clients
                          </Typography>
                        </Box>
                      </Box>
                      <FormattedMontant 
                        montant={totaux.impayes?.total_ttc || 0}
                        variant="body2"
                        color="#F44336"
                      />
                    </Box>
                    
                    <Divider style={{ margin: theme.spacing(1.5, 0) }} />
                    
                    {/* Total HT */}
                    <Box style={{ marginBottom: theme.spacing(1), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        Total HT
                      </Typography>
                      <FormattedMontant 
                        montant={totaux.global?.total_ht || 0}
                        variant="body2"
                        color="#1976d2"
                      />
                    </Box>
                    
                    {/* Total TVA */}
                    <Box style={{ marginBottom: theme.spacing(1), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        Total TVA
                      </Typography>
                      <FormattedMontant 
                        montant={totaux.global?.total_tva || 0}
                        variant="body2"
                        color="#2e7d32"
                      />
                    </Box>
                    
                    {/* Total TTC */}
                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        Total TTC
                      </Typography>
                      <FormattedMontant 
                        montant={totaux.global?.total_ttc || 0}
                        variant="body1"
                        color="#ef6c00"
                      />
                    </Box>
                    
                    {/* Total Remise */}
                    <Box style={{ marginTop: theme.spacing(1), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        Total Remise
                      </Typography>
                      <FormattedMontant 
                        montant={totaux.global?.total_remise || 0}
                        variant="body2"
                        color="#9C27B0"
                      />
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={error}
        action={
          <Button color="inherit" size="small" onClick={() => setSnackbarOpen(false)}>
            Fermer
          </Button>
        }
      />
    </Box>
  );
}

export default StatsBonClient;